import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import {useCopyDocumentMutation} from '../../data/documentWorkspaceApi';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Divider from '@mui/material/Divider';
import LoadingButton from '@mui/lab/LoadingButton';
import Radio from '@mui/material/Radio';
import Loading from '../Loading';
import {DARK_TEXT_COLOR, WHITE_100} from '../../theme';
import PropTypes from 'prop-types';
import {WORKBOOK_OPTIONS} from '../../constants';
import {decode} from 'html-entities';

const styles = {
  createButton: {
    background: '#26A993',
    borderRadius: '0.5rem',
    paddingLeft: '0.75rem',
    paddingRight: '1rem',
    paddingVertical: '0.375rem',
    height: '2.5rem',
    color: WHITE_100,
    '&:hover': {
      backgroundColor: '#229884',
    },
    '&:disabled': {
      backgroundColor: '#81C3B8',
    },
  },
  createButtonLabel: {
    fontSize: '0.875rem',
    lineHeight: '160%',
    fontWeight: '500',
    color: WHITE_100,
  },
  cancelButton: {
    borderWidth: 0,
    paddingVertical: '0.375rem',
    paddingHorizontal: '0.75rem',
    height: '2.5rem',
    '&:hover': {
      backgroundColor: '#F2F5F4',
      borderRadius: '0.5rem',
      borderWidth: 0,
    },
  },
  cancelButtonLabel: {
    color: '#032321',
    fontSize: '0.875rem',
    fontWeight: '500',
    lineHeight: '160%',
  },
  modal: {
    title: {
      fontSize: '1rem',
      fontWeight: '700',
      lineHeight: '150%',
      color: DARK_TEXT_COLOR,
      letterSpacing: '-0.16px',
    },
    divider: {
      borderBottomWidth: '0.0625rem',
      opacity: 1,
      color: '#CDD3D3',
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '0.5rem',
        height: '2.625rem',
        '& fieldset': {
          borderColor: '#CDD3D3',
        },
        '&:hover fieldset': {
          borderColor: '#26A993',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#26A993',
        },
      },
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      lineHeight: '130%',
      letterSpacing: '-0.14px',
      color: DARK_TEXT_COLOR,
    },
    workbookOption: {
      container: {
        cursor: 'pointer',
        borderRadius: '0.5rem',
        border: '2px solid #E1E5E4',
        background: WHITE_100,
        paddingTop: '0.75rem',
        paddingBottom: '0.75rem',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        '&:hover': {
          borderColor: '#34A290',
        },
      },
      selectedContainer: {
        cursor: 'pointer',
        borderRadius: '0.5rem',
        border: '2px solid #34A290',
        background: WHITE_100,
        paddingTop: '0.75rem',
        paddingBottom: '0.75rem',
        paddingLeft: '1rem',
        paddingRight: '1rem',
      },
      radio: {
        padding: 0,
        color: '#E1E5E4',
        '&.Mui-checked': {
          backgroundColor: '#34A290',
          borderColor: '#34A290',
          borderRadius: '50%',
          padding: 0,
          color: '#34A290',
          '& .MuiSvgIcon-root': {
            '&:last-child': {
              transform: 'scale(1.2)',
              color: '#FFFFFF',
            },
          },
        },
      },
      label: {
        fontSize: '0.875rem',
        fontWeight: '500',
        lineHeight: '130%',
        letterSpacing: '-0.14px',
        color: DARK_TEXT_COLOR,
      },
      helperText: {
        fontSize: '0.75rem',
        fontWeight: '500',
        lineHeight: '140%',
        letterSpacing: '-0.12px',
        color: '#819190',
      },
    },
  },
};

const CopyDocumentModal = ({
  open,
  onClose,
  onSuccess,
  onError,
  sourceDocument,
}) => {
  const [loading, setLoading] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [workbookOption, setWorkbookOption] = useState(
    WORKBOOK_OPTIONS.existing
  );
  const [copyDocument, {reset}] = useCopyDocumentMutation();

  // Reset state when modal opens
  useEffect(() => {
    if (open && sourceDocument) {
      const decodedName = decode(sourceDocument.name);
      setDocumentName(`Copy of ${decodedName}`);
      setWorkbookOption(WORKBOOK_OPTIONS.existing);
      setLoading(false);
    }
  }, [open, sourceDocument]);

  const _handleDocumentNameChange = (event) => {
    setDocumentName(event.target.value);
  };

  const _handleCopyDocument = async () => {
    setLoading(true);

    try {
      const result = await copyDocument({
        documentId: sourceDocument.documentId,
        name: documentName.trim(),
        workbookOption,
      });

      if (!_.has(result, 'error')) {
        onSuccess(`Document "${documentName.trim()}" created successfully`);
        onClose();
        reset();
      } else {
        console.error('Error copying document', {error: result.error});
        if (_.get(result.error, 'data.code') === 'DOCUMENT_EXISTS') {
          onError(
            'Filename exists in this organization. Please choose another.'
          );
        } else {
          onError(
            _.get(
              result.error,
              'data.error',
              'There was an error copying the document. Please try again.'
            )
          );
        }
        reset();
      }
    } catch (error) {
      console.error('Error copying document', {error});
      if (_.get(error, 'data.code') === 'DOCUMENT_EXISTS') {
        onError('Filename exists in this organization. Please choose another.');
      } else {
        onError('There was an error copying the document. Please try again.');
      }
      reset();
    } finally {
      setLoading(false);
    }
  };

  const _validateInputs = () => {
    return !_.isEmpty(documentName.trim());
  };

  const _onClose = () => {
    onClose();
  };

  if (!sourceDocument) {
    return null;
  }

  return (
    <Modal open={open} onClose={_onClose}>
      <Box
        position={'absolute'}
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box
          width={'31.25rem'}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '0.75rem',
            boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
            backgroundColor: '#FFFFFF',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Stack
            direction={'row'}
            paddingY={'1rem'}
            paddingX={'1.5rem'}
            justifyContent={'space-between'}
            alignItems={'center'}
            borderBottom={'1px solid #CDD3D3'}
          >
            <Typography sx={styles.modal.title}>Make a copy</Typography>
            <IconButton size={'small'} onClick={_onClose}>
              <CloseIcon fontSize={'small'} />
            </IconButton>
          </Stack>

          {/* Content */}
          <Stack direction={'column'} padding={'1.5rem'} spacing={'1.5rem'}>
            {/* Workbook Options */}
            <Stack direction={'column'} spacing={'0.5rem'}>
              <Typography style={styles.modal.label}>
                Workbook options
              </Typography>

              <Stack direction={'column'} spacing={'1rem'}>
                {/* Option 1: Use same workbook */}
                <Box
                  onClick={() => setWorkbookOption(WORKBOOK_OPTIONS.existing)}
                  sx={
                    workbookOption === WORKBOOK_OPTIONS.existing
                      ? styles.modal.workbookOption.selectedContainer
                      : styles.modal.workbookOption.container
                  }
                >
                  <Stack
                    direction={'row'}
                    spacing={'0.75rem'}
                    alignItems={'flex-start'}
                  >
                    <Radio
                      sx={styles.modal.workbookOption.radio}
                      checked={workbookOption === WORKBOOK_OPTIONS.existing}
                      value={WORKBOOK_OPTIONS.existing}
                      name="workbook-option"
                    />
                    <Stack direction={'column'} spacing={'0.125rem'}>
                      <Typography style={styles.modal.workbookOption.label}>
                        Use the same workbook
                      </Typography>
                      <Typography
                        style={styles.modal.workbookOption.helperText}
                      >
                        Both documents will share the same workbook. Changes in
                        the existing workbook sync to both documents.
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                {/* Option 2: Create new workbook */}
                <Box
                  onClick={() => setWorkbookOption(WORKBOOK_OPTIONS.new)}
                  sx={
                    workbookOption === WORKBOOK_OPTIONS.new
                      ? styles.modal.workbookOption.selectedContainer
                      : styles.modal.workbookOption.container
                  }
                >
                  <Stack
                    direction={'row'}
                    spacing={'0.75rem'}
                    alignItems={'flex-start'}
                  >
                    <Radio
                      sx={styles.modal.workbookOption.radio}
                      checked={workbookOption === WORKBOOK_OPTIONS.new}
                      value={WORKBOOK_OPTIONS.new}
                      name="workbook-option"
                    />
                    <Stack direction={'column'} spacing={'0.125rem'}>
                      <Typography style={styles.modal.workbookOption.label}>
                        Create a new workbook
                      </Typography>
                      <Typography
                        style={styles.modal.workbookOption.helperText}
                      >
                        A new workbook is created. Changes in the existing
                        workbook will not sync to the new document.
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Stack>

            {/* Document Name */}
            <Stack direction={'column'} spacing={'0.5rem'}>
              <Typography style={styles.modal.label}>
                New Report name *
              </Typography>
              <TextField
                sx={styles.modal.textField}
                id="copyDocument-documentName"
                variant="outlined"
                value={documentName}
                onChange={_handleDocumentNameChange}
                fullWidth
              />
            </Stack>
          </Stack>

          {/* Footer */}
          <Divider orientation={'horizontal'} sx={styles.modal.divider} />
          <Stack
            direction={'row'}
            paddingY={'1rem'}
            paddingX={'1.5rem'}
            justifyContent={'flex-end'}
            alignItems={'center'}
            spacing={'1.5rem'}
          >
            <Button
              variant={'outlined'}
              sx={styles.cancelButton}
              onClick={_onClose}
              disabled={loading}
            >
              <Typography style={styles.cancelButtonLabel}>Cancel</Typography>
            </Button>
            <LoadingButton
              variant={'contained'}
              sx={styles.createButton}
              onClick={_handleCopyDocument}
              disabled={!_validateInputs()}
              loading={loading}
              loadingIndicator={
                <Loading loading={loading} loadingProps={{color: WHITE_100}} />
              }
            >
              {!loading && (
                <Typography style={styles.createButtonLabel}>
                  Create a copy
                </Typography>
              )}
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

CopyDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  sourceDocument: PropTypes.shape({
    documentId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
};

export default CopyDocumentModal;
